import { isBefore } from 'date-fns';
import { createDraftSafeSelector } from '@reduxjs/toolkit';

import type { RootState } from 'App/Store';

import selfServicesApi from 'modules/selfServices/service';
import { RedirectPathConfig } from 'modules/auth/utils';

import {
  getOrigin, isPublicDevice, getContext as getDealerContext, getContext, isKioskOrigin,
} from 'modules/dealers/selectors';
import { getMFAData, getSelectedSelfServiceId } from 'modules/auth/selectors';

import {
  FlowType, SelfService, SelfServiceOrigin, SelfServiceType,
} from './types/SelfService';

const getRoot = (state: RootState) => state;
const getProps = (_: RootState, props: string) => props;
const getSelfServiceState = (state: RootState) => state.selfServices;

export const getSelectedSelfService = createDraftSafeSelector(
  getRoot,
  getSelectedSelfServiceId,
  (state, selectedSelfServiceId) => {
    const { data } = selfServicesApi.endpoints.getSelfServiceById.select({
      id: selectedSelfServiceId,
    })(state);
    return data;
  },
);

export const isSelfServiceLoading = createDraftSafeSelector(
  getRoot,
  getSelectedSelfServiceId,
  (state, selectedSelfServiceId) => {
    const { isLoading } = selfServicesApi.endpoints.getSelfServiceById.select({
      id: selectedSelfServiceId,
    })(state);
    return isLoading;
  },
);

export const getCustomerInfo = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.customerInfo,
);

export const getCustomerLanguage = createDraftSafeSelector(
  getCustomerInfo,
  (customerInfo) => customerInfo?.language,
);

export const getParkingInfo = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.parking,
);

export const getFinalInvoice = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.finalInvoice,
);

export const getSelfServiceType = createDraftSafeSelector(
  getContext,
  getSelectedSelfService,
  (context, selfService) => selfService?.type ?? context?.type,
);

export const isCheckoutSelfService = createDraftSafeSelector(
  getSelfServiceType,
  (type) => type === SelfServiceType.CHECK_OUT,
);

export const isCheckinSelfService = createDraftSafeSelector(
  getSelfServiceType,
  (type) => type === SelfServiceType.CHECK_IN,
);

export const isBreakdownSelfService = createDraftSafeSelector(
  getSelfServiceType,
  (type) => type === SelfServiceType.BREAKDOWN,
);

export const isFinalInvoicePayable = createDraftSafeSelector(
  getFinalInvoice,
  (finalInvoice) => Boolean(finalInvoice?.payable),
);

export const isInvoicePaymentSourceDelayable = createDraftSafeSelector(
  getOrigin,
  (origin) => origin !== SelfServiceOrigin.KIOSK,
);

export const isInvoicePaymentDelayable = createDraftSafeSelector(
  isFinalInvoicePayable,
  isCheckoutSelfService,
  isInvoicePaymentSourceDelayable,
  (isPayable, isCheckout, delayableType) =>
    isPayable && isCheckout && delayableType,
);

export const hasClickableInstruction = createDraftSafeSelector(
  getOrigin,
  isPublicDevice,
  isCheckinSelfService,
  isCheckoutSelfService,
  (origin, isDealerDevice, isCheckin, isCheckout) => (
    (isCheckin || isCheckout) && (
      origin === SelfServiceOrigin.HOME
        || (origin === SelfServiceOrigin.ON_SITE && !isDealerDevice)
        || (origin === SelfServiceOrigin.KIOSK && !isDealerDevice)
    )
  ),
);

export const getSelfServiceStatus = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.status,
);

// This is useful to know if the mobility WAS accepted before, hence there are new documents to send
export const isMobilityAccepted = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.mobility?.customerAccepted === true,
);

export const getMobilityDocuments = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.mobility?.documents ?? [],
);

export const shouldDisplayMobilityParking = (selfService: SelfService) => selfService?.mobilityEnabled
  && selfService.type === SelfServiceType.CHECK_OUT;

export const displayMobilityParking = createDraftSafeSelector(
  getSelectedSelfService,
  shouldDisplayMobilityParking,
);

export const getMobilityVehicle = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.mobility?.vehicle,
);

export const getParkingMaps = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.parkingMap?.maps,
);

export const isMobilityEnabled = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.mobilityEnabled,
);

export const isCourtesyVehicleCheckEnabled = createDraftSafeSelector(
  getMFAData,
  getDealerContext,
  isMobilityEnabled,
  (mfa, context, hasMobility) => (
    (mfa?.mobility?.courtesyVehicleCheckEnabled || context?.mobility?.courtesyVehicleCheckEnabled) && hasMobility
  ),
);

export const getBreakdownServiceAuthorType = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.breakdownServiceAuthorType,
);

export const getPayerById = createDraftSafeSelector(
  getSelectedSelfService,
  getProps,
  ({ payers }, payerId) => payers?.find(({ id }) => id === payerId),
);

export const getPayers = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.payers,
);

const getSelfServiceDealerId = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.dealer?.id,
);

const getCurrentKioskDealer = createDraftSafeSelector(
  getDealerContext,
  getSelfServiceDealerId,
  (context, dealerId) => context?.kioskDealers?.find(({ id }) => id === dealerId),
);

export const getKioskExplanationMedias = createDraftSafeSelector(
  getDealerContext,
  getCurrentKioskDealer,
  (context, dealer) => context.selfServiceKioskExplanationMedias ?? dealer?.selfServiceKioskExplanationMedias,
);

export const getCountry = createDraftSafeSelector(
  getDealerContext,
  getCurrentKioskDealer,
  (context, dealer) => {
    if (!context?.dealerCountry) {
      return dealer?.dealerCountry ?? context?.kioskDealers?.[0]?.dealerCountry;
    }
    return context?.dealerCountry;
  },
);

export const getCurrencyIsoCode = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService.dealer.currencyIsoCode,
);

export const getValueAddedTax = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService.valueAddedTax,
);

export const isFlowTypeModalOpen = createDraftSafeSelector(
  getSelfServiceState,
  ({ isFlowTypeModalOpen: isOpen }) => isOpen,
);

export const isFlowTypeModalVisible = createDraftSafeSelector(
  isFlowTypeModalOpen,
  getSelectedSelfService,
  (isOpen, selfService) => isOpen && Boolean(selfService),
);

export const getFlowType = createDraftSafeSelector(
  getSelfServiceState,
  ({ flowType }) => flowType,
);

export const isQuickFlow = createDraftSafeSelector(
  getFlowType,
  (flowType) => flowType === FlowType.QUICK,
);

export const getRedirectUrl = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => RedirectPathConfig[selfService.type],
);

export const getLegalNotice = createDraftSafeSelector(
  getContext,
  getSelectedSelfService,
  (context, selfService) => {
    const legalNoticeFromDealer = context?.kioskDealers?.length === 1 && context?.kioskDealers?.at(0)?.legalNotice;
    const legalNotice = context?.legalNotice ?? legalNoticeFromDealer;
    return selfService?.legalNotice ?? legalNotice;
  },
);

export const getSummarySignatureEnabled = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.dealer?.summarySignatureEnabled ?? false,
);

export const canReopenKiosk = createDraftSafeSelector(
  getSelectedSelfService,
  isKioskOrigin,
  (selfService, isOriginKiosk) => (
    isOriginKiosk
      && selfService?.reopen?.allowed
      && (!selfService.reopen.until || isBefore(Date.now(), selfService.reopen.until))
  ),
);

export const getUntilDateToReopenKiosk = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.reopen?.until,
);

const getUnits = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService?.dealer?.units,
);

export const getSmallDistanceUnit = createDraftSafeSelector(
  getUnits,
  (units) => units?.smallDistanceUnit,
);

export const getLargeDistanceUnit = createDraftSafeSelector(
  getUnits,
  (units) => units?.largeDistanceUnit,
);

export const isAutoplayEnabled = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService.dealer.allowAutoPlay,
);

export const arePreApprovedPricesHidden = createDraftSafeSelector(
  getSelectedSelfService,
  (selfService) => selfService.dealer.hidePreApprovedWorksPrices,
);
